import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import NukaCarousel from 'nuka-carousel';

import useScreen from 'hooks/useScreen';
import getSlideWidth from './helpers/getSlideWidth';

const MAX_SLIDER_DOTS = 7;

// This function ensures that we show a maximum of 7 slider dots
const getDotsToShow = (allSlidesArray = [], currentSlide) => {
  const maxDotsEitherSide = Math.floor(MAX_SLIDER_DOTS / 2);

  if (!currentSlide || allSlidesArray.length <= MAX_SLIDER_DOTS) {
    return allSlidesArray.slice(0, MAX_SLIDER_DOTS);
  }

  if (currentSlide + maxDotsEitherSide >= allSlidesArray.length) {
    return allSlidesArray.slice(allSlidesArray.length - MAX_SLIDER_DOTS);
  }

  if (currentSlide >= maxDotsEitherSide) {
    return allSlidesArray.slice(
      currentSlide - maxDotsEitherSide,
      currentSlide + maxDotsEitherSide + 1
    );
  }

  return allSlidesArray.slice(0, MAX_SLIDER_DOTS);
};

const Carousel = ({
  children,
  selectedDotColor = 'white',
  afterSlide,
  disableEdgeSwiping = false,
}) => {
  const { windowWidth, isMobile, isMobileOrTablet } = useScreen();
  const slideWidth = getSlideWidth({ isMobile, isMobileOrTablet, windowWidth });

  return (
    <div className="relative transition-all duration-500 ease-in-out w-full h-full">
      {children && (
        <NukaCarousel
          className="m-0 p-0 relative"
          afterSlide={afterSlide}
          slidesToShow={1}
          cellSpacing={0}
          dragging
          width="100%"
          initialSlideHeight={slideWidth}
          initialSlideWidth={slideWidth}
          renderCenterLeftControls={() => null}
          renderCenterRightControls={() => null}
          edgeEasing="easePolyIn"
          disableEdgeSwiping={disableEdgeSwiping}
          renderBottomCenterControls={({
            slideCount = 4,
            currentSlide = 1,
          }) => {
            const allSlidesCountArray = Array.from(Array(slideCount).keys());
            const allSlidesArray = allSlidesCountArray.map(index => ({
              slideIndex: index,
            }));
            const dotsToShow = getDotsToShow(allSlidesArray, currentSlide);

            return (
              <div className="block w-full p-0 m-0 list-none text-center relative bottom-0">
                {dotsToShow.map(({ slideIndex }) => (
                  <span
                    key={slideIndex}
                    className={classNames(
                      'border-0 rounded-full h-1.5 w-1.5 m-1 relative inline-block opacity-60 transition-all duration-700 ease-out cursor-pointer first:scale-50 last:scale-50 [&:nth-child(2)]:scale-75 [&:nth-last-child(2)]:scale-75 only:scale-100',
                      {
                        'opacity-100 scale-100': currentSlide === slideIndex,
                      },
                      {
                        'bg-whiteOverlay disabled:bg-white':
                          selectedDotColor === 'white',
                        'bg-white':
                          selectedDotColor === 'white' &&
                          currentSlide === slideIndex,
                      },
                      {
                        'bg-gray-light6 disabled:bg-brand':
                          selectedDotColor === 'brand',
                        'bg-brand':
                          selectedDotColor === 'brand' &&
                          currentSlide === slideIndex,
                      },
                      {
                        'bg-gray-light4 disabled:bg-gray-light2':
                          selectedDotColor === 'grey',
                        'bg-gray-light2':
                          selectedDotColor === 'grey' &&
                          currentSlide === slideIndex,
                      }
                    )}
                  >
                    <span
                      className={classNames(
                        `inline-block h-5 w-full max-w-7.5`
                      )}
                    />
                  </span>
                ))}
              </div>
            );
          }}
        >
          {children}
        </NukaCarousel>
      )}
    </div>
  );
};

Carousel.propTypes = {
  children: PropTypes.node,
  selectedDotColor: PropTypes.oneOf(['white', 'brand', 'grey']),
  afterSlide: PropTypes.func,
  disableEdgeSwiping: PropTypes.bool,
};

export default Carousel;
